import React, {useState, useEffect, useContext} from 'react';
import {
  IconButton,
  Popover,
  Box,
  Typography,
  List,
  ListItemText,
  ListItemIcon,
  Button,
  Divider,
  Badge,
  ListItemButton
} from '@mui/material';
import {
  IconBell,
  IconAlertSmall,
  IconAlertCircle,
  IconX, IconMessage2
} from '@tabler/icons-react';
import {NotificationsRepository} from '../../../repositories/NotificationsRepository';
import {SignInContext} from '../../../context/SignInContext';
import {toast} from 'react-toastify';
import moment from "moment";
import {blue, orange} from "@mui/material/colors";
import {Link} from "react-router-dom";
import {useStompWebsocket} from "../../../hooks/useStompWebsocket";

export const NotificationButton = ({project}) => {
  const {currentUser} = useContext(SignInContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalElements, setTotalElements] = useState(0);
  const [updatedValue] = useStompWebsocket(project?.id ? `/topic/notifications/${project?.id}`: null)

  const open = Boolean(anchorEl);

  const loadNotifications = async (pageNum = 0, append = false) => {
    if (!project?.id) return;

    try {
      setLoading(!append);
      setLoadingMore(append);

      const response = await NotificationsRepository.getAllNotifications({
        flowUid: project.id,
        page: pageNum,
        size: 5
      });

      const data = response.data;
      const newNotifications = data.content || [];

      if (append) {
        setNotifications(prev => [...prev, ...newNotifications]);
      } else {
        setNotifications(newNotifications);
      }

      setHasMore(!data.last);
      setTotalElements(data.totalElements || 0);
      setPage(pageNum);
    } catch (error) {
      console.error('Error loading notifications:', error);
      // toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (project?.id && currentUser?.username) {
      loadNotifications(0);
      NotificationsRepository.getNotificationsCount({flowUid: project?.id, username: currentUser?.username})
        .then((response) => {
          setUnreadCount(response?.data || 0)
        })
        .catch(error => {
          console.log(error);
          setUnreadCount(0);
        })
    }
  }, [project?.id, currentUser, updatedValue]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLoadMore = () => {
    if (hasMore && !loadingMore) {
      loadNotifications(page + 1, true);
    }
  };

  const handleMarkAsRead = async (notification) => {
    console.log(notification, notification?.viewedBy?.includes(currentUser?.username))
    if (notification.viewedBy?.includes(currentUser?.username) || !currentUser?.username) {
      handleClose();
      return;
    }

    try {
      await NotificationsRepository.markNotificationsAsViewed({
        notificationIds: [notification.id],
        username: currentUser.username
      });

      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notification.id ? {
            ...notif,
            viewedBy: [...(notif?.viewedBy || []), currentUser?.username]
          } : notif
        )
      );
      handleClose();
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Failed to mark notification as read');
    }
  };

  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0 || !currentUser?.username) return;
    const unreadNotifications = notifications.filter(n => !n?.viewedBy?.includes(currentUser?.username)) || [];

    try {
      await NotificationsRepository.markNotificationsAsViewed({
        notificationIds: unreadNotifications.map(n => n.id) || [],
        username: currentUser.username,
        flowUid: project?.id
      });

      setNotifications(prev =>
        prev.map(notif => ({...notif, viewedBy: [...(notif?.viewedBy || []), currentUser?.username]}))
      );

      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking notifications as read:', error);
      toast.error('Failed to mark notifications as read');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'COMMENT':
        return <IconMessage2 size={25} color={blue[500]}/>;
      case 'USER_NOT_TALKING':
        return <IconAlertCircle size={25} color={orange[500]}/>;
      case 'error':
        return <IconX size={25} color="#f44336"/>;
      default:
        return <IconAlertSmall size={25} color="#2196f3"/>;
    }
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        sx={{
          color: 'dark.main',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <IconBell size={20}/>
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              width: 400,
              maxWidth: '90vw',
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
              border: '1px solid',
              borderColor: 'divider'
            }
          }
        }}
      >
        <Box>
          {/* Header */}
          <Box
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Button
                size="small"
                variant="ghost"
                onClick={handleMarkAllAsRead}
                sx={{minWidth: 'auto', fontSize: "0.75em", px: 1}}
              >
                Mark All as Read
              </Button>
            )}
          </Box>

          {/* Notification List */}
          <Box sx={{maxHeight: 400, overflow: 'auto'}}>
            {loading && notifications.length === 0 ? (
              <Box
                sx={{
                  p: 4,
                  textAlign: 'center',
                  color: 'text.secondary'
                }}
              >
                <Typography variant="body1">
                  Loading notifications...
                </Typography>
              </Box>
            ) : notifications.length === 0 ? (
              <Box
                sx={{
                  p: 4,
                  textAlign: 'center',
                  color: 'text.secondary'
                }}
              >
                <IconBell size={48} style={{opacity: 0.3, marginBottom: 16}}/>
                <Typography variant="body1" gutterBottom>
                  No notifications
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  You're all caught up! New notifications will appear here.
                </Typography>
              </Box>
            ) : (
              <List sx={{p: 0}}>
                {notifications.map((notification, index) => (
                  <React.Fragment key={notification.id}>
                    <ListItemButton
                      component={Link}
                      to={`/project/${project?.id}/supervision/${notification?.conversationId}`}
                      sx={{
                        py: 2,
                        px: 2,
                        backgroundColor: notification.viewedBy?.includes(currentUser?.username) ? 'transparent' : 'action.hover',
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        },
                        cursor: 'pointer'
                      }}
                      onClick={() => handleMarkAsRead(notification)}
                    >
                      <ListItemIcon sx={{minWidth: 40}}>
                        {getNotificationIcon(notification.type || 'info')}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{mb: 0.5, fontSize: "0.85em"}}
                          >
                            {notification.description || notification.content}
                          </Typography>
                        }
                        secondary={
                          <>

                            <Typography variant="caption" align={"right"} fontSize={"0.7em"} color="text.secondary">
                              {moment(notification?.dateCreated)?.fromNow()}
                            </Typography>
                          </>
                        }
                      />
                      {!notification.viewedBy?.includes(currentUser?.username) && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: 'primary.main',
                            ml: 1
                          }}
                        />
                      )}
                    </ListItemButton>
                    {index < notifications.length - 1 && <Divider/>}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>

          {/* Footer - Load More Button */}
          {notifications.length > 0 && (
            <>
              <Divider/>
              <Box sx={{p: 2}}>
                <Button
                  fullWidth
                  variant="ghost"
                  onClick={handleLoadMore}
                  disabled={loadingMore || !hasMore}
                  sx={{
                    borderColor: 'divider',
                    color: 'text.primary',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  {loadingMore ? 'Loading...' : 'Load More'}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Popover>
    </>
  );
};